

























import {
  defineComponent,
  defineAsyncComponent,
  computed,
  PropType,
  watch,
  nextTick,
} from '@vue/composition-api';
import { Select, SelectOptionType } from '@hb/coffee';
import { ubtSetupModuleExpose, ubtSetupPageButton } from '@/shared/utils/ubt/ubt-setup';
import { LoanUsageOption } from '@/api/apply-product/types';
import { TP_QUICK_WITHDRAW_CARD_LOAN_PURPOSE_DETAIL } from '../../tianqi';

const SelectView = defineAsyncComponent(() => Promise.resolve(Select.view));

export interface PurposeOption {
  value: string;
  label: string;
  badge?: string;
}

export default defineComponent({
  components: {
    SelectView,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    currentSelectPurpose: {
      type: Object as PropType<PurposeOption>,
      default: () => ({}),
    },
    loanPurposeOptions: {
      type: Array as PropType<LoanUsageOption[]>,
      default: () => [],
    },
    containerHeight: {
      type: Number,
      default: 0,
    },
  },
  emits: ['cancel', 'updateSelectPurpose', 'updateVisible'],
  setup(props, context) {
    /** 借款用途选项 - 使用动态数据，如果没有则使用默认选项 */
    const purposeOptions = computed(() => {
      if (props.loanPurposeOptions && props.loanPurposeOptions.length > 0) {
        return props.loanPurposeOptions.map((option) => ({
          value: option.code,
          label: option.name,
        }));
      }

      // 默认选项作为后备
      return [
        { value: 'shopping', label: '购物消费' },
      ];
    });

    /** 当前选中的借款用途 - 使用计算属性与 props 保持同步 */
    const selectedPurpose = computed(() => props.currentSelectPurpose?.value || 'shopping');

    /** 选择借款用途选项，转换为Select组件需要的格式 */
    const selectPurposeOptions = computed(() => purposeOptions.value.map((item, index) => ({
      key: item.value,
      value: item.label,
      index,
    })));

    /** 当前已选中的借款用途，用于默认选中 */
    const defaultSelectPurposeValues = computed(() => selectedPurpose.value);

    /** 当前选中用途的标签 */
    const selectedPurposeLabel = computed(() => {
      const option = purposeOptions.value.find((item) => item.value === selectedPurpose.value);
      return option?.label || '购物消费';
    });

    const onCancel = () => {
      context.emit('cancel');
    };


    /** 选择借款用途前，检查是否拦截切换选择用途 */
    const onBeforeChecked = async (checkedOption: SelectOptionType) => {
      const selectingPurpose = purposeOptions.value.find(
        (item) => item.value === checkedOption.key,
      ) as PurposeOption;
      ubtSetupPageButton(TP_QUICK_WITHDRAW_CARD_LOAN_PURPOSE_DETAIL, {
        Content: selectingPurpose.label,
      });

      if (selectingPurpose) {
        // 触发选择事件，通知父组件更新值
        context.emit('updateSelectPurpose', selectingPurpose);

        // 关闭弹窗
        context.emit('updateVisible', false);
      }

      return true;
    };

    // 监听弹窗显示状态，触发曝光埋点
    watch(
      () => props.visible,
      (isVisible) => {
        if (isVisible) {
          ubtSetupModuleExpose(TP_QUICK_WITHDRAW_CARD_LOAN_PURPOSE_DETAIL);
        } else {
          ubtSetupPageButton(TP_QUICK_WITHDRAW_CARD_LOAN_PURPOSE_DETAIL, { Content: '关闭弹窗' });
        }
      },
    );

    // 监听visible和containerHeight变化，动态设置SelectView容器高度
    watch(
      [() => props.visible, () => props.containerHeight],
      async ([visible, containerHeight]) => {
        await nextTick();
        const selectContainer = document.querySelector('.c-select__container');
        if (selectContainer) {
          const el = selectContainer as HTMLElement;
          if (visible) {
            // 弹窗显示时设置动态高度与底部安全区
            if (containerHeight) {
              el.style.setProperty('height', `${containerHeight}px`, 'important');
            }
            // 底部安全区
            el.style.setProperty('margin-bottom', 'env(safe-area-inset-bottom)', 'important');
          } else {
            // 弹窗关闭时重置样式
            el.style.removeProperty('height');
            el.style.removeProperty('margin-bottom');
          }
        }
      },
      { immediate: true },
    );

    return {
      purposeOptions,
      selectedPurpose,
      selectPurposeOptions,
      defaultSelectPurposeValues,
      selectedPurposeLabel,
      onBeforeChecked,
      onCancel,
      containerHeight: props.containerHeight,
    };
  },
});
